/**
 * data/providerRegistry.ts
 * ─────────────────────────────────────────────────────────────────────────
 * Canonical provider registry — the SINGLE source of truth for which voice
 * providers exist, keyed by the exact slug the sk-voice runtime switches on
 * (its adapter-factory `case` strings). The DB stores these slugs; the UI
 * renders the human `label`. Only providers the runtime can ACTUALLY run are
 * listed here — no aspirational options that would persist but never honor.
 *
 * Runtime source of truth (sk-voice):
 *   STT      → STTAdapterFactory:      'sarvam' | 'deepgram'
 *   LLM      → LLMAdapterFactory:      'groq' | 'openai'   (claude removed — too slow)
 *   TTS      → TTSAdapterFactory:      'google' | 'sarvam' | 'elevenlabs'
 *   UNIFIED  → UnifiedAdapterFactory:  'gemini_live'       (primary; Gemini 3.1)
 */

export interface ProviderOption {
  slug: string;   // canonical id — stored in DB, matched by the runtime
  label: string;  // human display in the UI
}

// Composable-pipeline providers (secondary path).
export const STT_PROVIDERS: ProviderOption[] = [
  { slug: 'sarvam', label: 'Sarvam (saarika:v2)' },
  { slug: 'deepgram', label: 'Deepgram (nova-2)' },
];
export const LLM_PROVIDERS: ProviderOption[] = [
  { slug: 'groq', label: 'Groq (llama-4-scout)' },
  { slug: 'openai', label: 'OpenAI (gpt-4o-mini)' },
];
export const TTS_PROVIDERS: ProviderOption[] = [
  { slug: 'google', label: 'Google (Chirp3-HD)' },
  { slug: 'sarvam', label: 'Sarvam (bulbul)' },
  { slug: 'elevenlabs', label: 'ElevenLabs' },
];
// Unified-pipeline providers (PRIMARY path — Gemini 3.1).
export const UNIFIED_PROVIDERS: ProviderOption[] = [
  { slug: 'gemini_live', label: 'Gemini Live (3.1)' },
];

const bySlug = (opts: ProviderOption[]) => new Map(opts.map((o) => [o.slug, o]));
const STT = bySlug(STT_PROVIDERS), LLM = bySlug(LLM_PROVIDERS), TTS = bySlug(TTS_PROVIDERS), UNI = bySlug(UNIFIED_PROVIDERS);

// slug → label for rendering. Unknown slug (e.g. a legacy label-row not yet
// normalized) falls back to itself so nothing renders blank.
export function providerLabel(kind: 'stt' | 'llm' | 'tts' | 'unified', slug: string): string {
  const m = kind === 'stt' ? STT : kind === 'llm' ? LLM : kind === 'tts' ? TTS : UNI;
  return m.get(slug)?.label ?? slug;
}

// The label lists the dropdowns render.
export const STT_LABELS = STT_PROVIDERS.map((o) => o.label);
export const LLM_LABELS = LLM_PROVIDERS.map((o) => o.label);
export const TTS_LABELS = TTS_PROVIDERS.map((o) => o.label);
export const UNIFIED_LABELS = UNIFIED_PROVIDERS.map((o) => o.label);

// label → slug for saving (invert the display).
export function providerSlug(kind: 'stt' | 'llm' | 'tts' | 'unified', label: string): string {
  const opts = kind === 'stt' ? STT_PROVIDERS : kind === 'llm' ? LLM_PROVIDERS : kind === 'tts' ? TTS_PROVIDERS : UNIFIED_PROVIDERS;
  return opts.find((o) => o.label === label)?.slug ?? label;
}
